import { useState, Fragment } from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { ToastManager } from '../../../ToastManager.jsx';
import { wait } from '../../../lib/wait.ts';
import { generalSettingsStore } from './GeneralSettingsStore.ts';

import { useApi } from '../../../store/SelectedHomeyStore.js';
import { useUser } from '../../../store/users/useUsers.ts';
import { useI18n } from '../../../hooks/useI18nFormatters.tsx';
import { useUserMe } from '../../../store/users/useUserMe.ts';

import { theme } from '../../../theme/theme.ts';
import { su } from '../../../theme/functions/su.js';
import { publicUrl } from '../../../theme/functions/publicUrl.js';

import { SpinnerFade } from '../../../components/common/SpinnerFade.jsx';
import { Dialog } from '../../../components/overlay/Dialog.jsx';
import { SolidButton } from '../../../components/buttons/SolidButton.tsx';
import { IconButton } from '../../../components/buttons/IconButton.tsx';
import { TextField } from '../../../components/forms/TextField.jsx';
import { Icon } from '../../../components/common/Icon.tsx';
import { RoundIconWrapper } from '../../../components/common/Icon.tsx';

import { iconCloseThin } from '../../../theme/icons/interface/close-thin/index.js';
import { iconNoSignal } from '../../../theme/icons-v2/interface/no-signal/no-signal.js';
import { iconPresence } from '../../../theme/icons-v2/system/presence/presence.js';
import { iconLock } from '../../../theme/icons-v2/core/lock.js';
import { iconCheckmarkCircleFill } from '../../../theme/icons-v2/interface/checkmark-circle-fill/checkmark-circle-fill.js';

export function FamilySettingsDialogLocalUser(props) {
  const { i18n } = useI18n();
  const params = useParams();
  const { api } = useApi();

  const { user } = useUser({ userId: params.userId });
  const { userMe } = useUserMe();
  const [view, setView] = useState(props.view ?? 'info');

  const [isSettingCredentials, setIsSettingCredentials] = useState(false);

  const { generalSettings } = generalSettingsStore.use();

  const { register, handleSubmit, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
    },
  });

  function handleClosePress() {
    props.onCloseRequest();
  }

  function onSubmit(data, options = {}) {
    setIsSettingCredentials(true);
    const minDuration = wait(500);

    if (user == null || userMe == null) {
      setIsSettingCredentials(false);
      ToastManager.handleError(new Error('Missing User or UserMe.'));
      return;
    }

    if (user.id !== userMe.id && userMe.role === 'owner') {
      api
        .call({
          method: 'PUT',
          path: `/api/manager/users/user/${user.id}/local-credentials`,
          body: {
            username: data.username,
            password: data.password,
          },
          timeout: 5 * 1000,
        })
        .then(async (res) => {
          await minDuration;
          props.onCloseRequest();
        })
        .catch((error) => {
          setIsSettingCredentials(false);
          ToastManager.handleError(error);
        })
        .finally(() => {});
      return;
    }

    api
      .call({
        method: 'PUT',
        path: `/api/manager/users/user/me/local-credentials`,
        body: {
          username: data.username,
          password: data.password,
        },
        timeout: 5 * 1000,
      })
      .then(async (res) => {
        await minDuration;
        props.onCloseRequest();
        ToastManager.add({
          icon: iconCheckmarkCircleFill,
          iconColor: theme.color.green,
          message: i18n.messageFormatter('settings.system.usersLocalUserDialogSuccessMessage', {
            address: (
              <a
                key="link"
                href={`http://${
                  generalSettings.systemInfoData?.ethernetAddress ??
                  generalSettings.systemInfoData?.wifiAddress ??
                  'x.x.x.x'
                }`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: theme.color.orange, textDecoration: 'none' }}
              >
                {`http://${
                  generalSettings.systemInfoData?.ethernetAddress ??
                  generalSettings.systemInfoData?.wifiAddress ??
                  'x.x.x.x'
                }`}
              </a>
            ),
          }),
        });
      })
      .catch((error) => {
        setIsSettingCredentials(false);
        ToastManager.handleError(error);
      })
      .finally(() => {});
  }

  return (
    <Dialog
      containFocus={true}
      // autoFocus={true}
      onClose={() => {
        props.onCloseRequest();
      }}
    >
      <S.Root>
        {view === 'info' && (
          <Fragment>
            <S.CloseIconButtonWrapper>
              <IconButton
                url={iconCloseThin}
                color={theme.color.icon_light}
                size={theme.icon.size_small}
                onPress={handleClosePress}
              />
            </S.CloseIconButtonWrapper>

            <RoundIconWrapper size="80px" color={theme.color.blue_o_05}>
              <Icon size="32px" url={iconLock} color={theme.color.blue} />
            </RoundIconWrapper>

            <S.Title>
              {i18n.messageFormatter('settings.system.usersLocalUserInfoDialogTitle')}
            </S.Title>

            <S.Info>
              {i18n.messageFormatter('settings.system.usersLocalUserInfoDialogSubtitle', {
                address: (
                  <a
                    key="link"
                    href={`http://${
                      generalSettings.systemInfoData?.ethernetAddress ??
                      generalSettings.systemInfoData?.wifiAddress ??
                      'x.x.x.x'
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: theme.color.orange, textDecoration: 'none' }}
                  >
                    {`http://${
                      generalSettings.systemInfoData?.ethernetAddress ??
                      generalSettings.systemInfoData?.wifiAddress ??
                      'x.x.x.x'
                    }`}
                  </a>
                ),
              })}
            </S.Info>

            <S.Features>
              <S.FeaturesRow>
                <Icon
                  url={iconNoSignal}
                  color={theme.color.icon_blue}
                  size={theme.icon.size_small}
                />
                <span>
                  {i18n.messageFormatter(
                    'settings.system.usersLocalUserInfoDialogFeatureWithoutInternet'
                  )}
                </span>
              </S.FeaturesRow>
              <S.FeaturesRow>
                <Icon
                  url={iconPresence}
                  color={theme.color.icon_blue}
                  size={theme.icon.size_small}
                />
                <span>
                  {i18n.messageFormatter('settings.system.usersLocalUserInfoDialogFeatureLocal')}
                </span>
              </S.FeaturesRow>
            </S.Features>

            <S.Actions>
              <SolidButton
                autoFocus={true}
                styleFlat={true}
                styleColor="blue"
                onPress={() => {
                  setView('edit');
                }}
              >
                {i18n.messageFormatter('common.continue')}
              </SolidButton>
            </S.Actions>
          </Fragment>
        )}

        {view === 'edit' && (
          <Fragment>
            <S.CloseIconButtonWrapper>
              <IconButton
                url={iconCloseThin}
                color={theme.color.icon_light}
                size={theme.icon.size_small}
                onPress={handleClosePress}
              />
            </S.CloseIconButtonWrapper>

            <S.Avatar src={user.avatar || publicUrl(`img/ph-64.png`)} />

            <S.Title>{i18n.messageFormatter('settings.system.usersLocalUserDialogTitle')}</S.Title>

            <S.Info>{i18n.messageFormatter('settings.system.usersLocalUserDialogSubtitle')}</S.Info>

            <S.Form id="local-user-form" onSubmit={handleSubmit(onSubmit)}>
              <TextField
                name="username"
                aria-label={i18n.messageFormatter('common.username')}
                placeholder={i18n.messageFormatter('common.username')}
                orientation="vertical"
                required={true}
                autoFocus={true}
                register={register}
              />
              <TextField
                name="password"
                type="password"
                aria-label={i18n.messageFormatter('common.password')}
                placeholder={i18n.messageFormatter('common.password')}
                orientation="vertical"
                required={true}
                minLength={8}
                register={register}
              />
            </S.Form>

            <S.Actions>
              <SolidButton
                form="local-user-form"
                type="submit"
                isDisabled={isSettingCredentials === true || formState.isValid === false}
                styleFlat={true}
                styleColor="blue"
                onPress={() => {}}
              >
                <SpinnerFade
                  isActive={isSettingCredentials === true}
                  color={theme.color.white}
                  size={theme.icon.size_small}
                />
                <SolidButton.Text>
                  {i18n.messageFormatter('settings.system.usersLocalUserDialogSaveButton')}
                </SolidButton.Text>
              </SolidButton>
            </S.Actions>
          </Fragment>
        )}
      </S.Root>
    </Dialog>
  );
}

function S() {}
FamilySettingsDialogLocalUser.S = S;

S.Root = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  width: 360px;
  padding: ${su(4)} ${su(2)} ${su(2)};
`;

S.CloseIconButtonWrapper = styled.div`
  position: absolute;
  right: ${su(2)};
  top: ${su(2)};
`;

S.Title = styled.h3`
  text-align: center;
  padding-top: ${su(2)};
`;

S.Info = styled.p`
  color: ${theme.color.text_light};
  text-align: center;
`;

S.Form = styled.form`
  width: 100%;
  padding-bottom: ${theme.su(2)};
  display: flex;
  flex-direction: column;
  gap: ${theme.su(1)};
`;

S.Avatar = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 10px;
  border-radius: 50%;
  box-shadow: ${theme.boxShadow.default};
`;

S.Actions = styled.div`
  display: grid;
  width: 100%;
  grid-auto-flow: column;
  grid-gap: ${su(1)};
`;

S.Features = styled.div`
  background-color: ${theme.color.blue_o_05};
  border-radius: ${theme.borderRadius.small};
  color: ${theme.color.blue};
  padding: ${su(1.5)} ${su(1)};
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${su(1)};
  margin-bottom: ${su(2)};
`;

S.FeaturesRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${su(1)};
`;
